import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner-native";

import {
  AddHouseholdMemberAndRelationshipType,
  HouseholdMemberUpdateType,
} from "@/domain/householdMember";
import { useTranslator } from "@/hooks/useTranslate";
import { queryClient } from "@/utils/queryClient";

import { HouseholdMembersApi } from "../api/householdMembersApi";
import { EligibilityCategoriesQueryKeys } from "../queries/eligibilityCategoriesQueries";
import { HouseholdMemberRelationshipsQueryKeys } from "../queries/householdMemberRelationshipsQueries";
import { HouseholdMembersQueryKeys } from "../queries/householdMembersQueries";
import { PatientsQueryKeys } from "../queries/patientsQueries";
import { UploadedDocumentQueryKeys } from "../queries/uploadedDocumentsQueries";

export const useUpdateHouseholdMemberMutation = () => {
  const { t } = useTranslator();

  return useMutation({
    mutationFn: async ({
      id,
      householdMemberData,
    }: {
      id: string;
      householdMemberData: HouseholdMemberUpdateType;
    }) => HouseholdMembersApi.updateHouseholdMember(id, householdMemberData),
    onError: () => {
      toast.error(t("native:memberFormSubmission.error"));
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: HouseholdMembersQueryKeys.all,
        }),
        queryClient.invalidateQueries({
          queryKey: HouseholdMemberRelationshipsQueryKeys.all,
        }),
        queryClient.invalidateQueries({
          queryKey: PatientsQueryKeys.me(),
        }),
        // Document completion can be derived from member data, such as an SSN.
        queryClient.invalidateQueries({
          queryKey: UploadedDocumentQueryKeys.requirements(),
        }),
        // Invalidate eligibility categories in case a member address update changes the resolved US state
        queryClient.invalidateQueries({
          queryKey: EligibilityCategoriesQueryKeys.all,
        }),
      ]);
    },
  });
};

export const useUpdateMedicaidCaseNumbersMutation = () => {
  const { t } = useTranslator();

  return useMutation({
    mutationFn: async (
      caseNumbers: { id: string; medicaidCaseNumber: string | null }[],
    ) => {
      const results = await Promise.allSettled(
        caseNumbers.map(({ id, medicaidCaseNumber }) =>
          HouseholdMembersApi.updateHouseholdMember(id, { medicaidCaseNumber }),
        ),
      );

      if (results.some(({ status }) => status === "rejected")) {
        throw new Error("Failed to update Medicaid case numbers");
      }
    },
    onError: () => {
      toast.error(t("native:memberFormSubmission.error"));
    },
    // Some members may have saved even when another one failed.
    onSettled: () => {
      void queryClient.invalidateQueries({
        queryKey: HouseholdMembersQueryKeys.all,
      });
    },
  });
};

export const useAddHouseholdMemberMutation = () => {
  const { t } = useTranslator();

  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: AddHouseholdMemberAndRelationshipType) =>
      HouseholdMembersApi.addHouseholdMember(data),
    onError: () => {
      toast.error(t("native:addHouseholdMemberScreen.formSubmission.error"));
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: HouseholdMembersQueryKeys.list(),
      });
    },
  });
};

export const useDeleteHouseholdMemberMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (householdMemberId: string) =>
      HouseholdMembersApi.deleteHouseholdMember(householdMemberId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: HouseholdMembersQueryKeys.list(),
      });
    },
  });
};
